import React, { useEffect, useState, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControl,
  RadioGroup,
  Container,
  Radio,
  AppBar, Toolbar,
  FormControlLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Box,
  Paper,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

// ─────────────────────────────────────────────
// Info data for the dialog
// ─────────────────────────────────────────────
const reportInfoData = [
  {
    title: "Student Attendance",
    color: "#1976D2",
    description:
      "Generates attendance records for students. Supports filtering by class, section, and individual student over a custom date range.",
    filters: [
      "All Class / Class Wise Student",
      "All Section / Select Section",
      "All Student / Select Student",
      "Date Range (From Date – To Date)",
    ],
    columns: [
      "Serial No",
      "Student Name",
      "Roll No",
      "Class",
      "Section",
      "Attendance Status",
      "Attendance Date",
    ],
    exports: ["PDF", "CSV"],
  },
  {
    title: "Employee Attendance",
    color: "#7B1FA2",
    description:
      "Generates attendance records for employees. Supports Summary and Detailed report types with filters for employee, session, department, designation, date and time range.",
    filters: [
      "Report Type (Summary / Detailed)",
      "Employee",
      "Session",
      "Department",
      "Designation",
      "Date Range (From Date – To Date)",
      "Time Range (From Time – To Time)",
    ],
    columns: {
      summary: [
        "S. No.",
        "Emp Name",
        "Emp Code",
        "Dept.",
        "Desig.",
        "Month",
        "Present",
        "Absent",
        "Leave",
        "Work Days",
        "Present %",
      ],
      detail: [
        "S. No.",
        "Emp Name",
        "Emp Code",
        "Dept",
        "Desig",
        "Attendance Date",
        "Day",
        "Status",
      ],
    },
    exports: ["PDF", "CSV"],
  },
];

const StudentAttendance = ({
  ddlClass,
  ddlSection,
  ddlStudent,
  selectedClass,
  selectedSection,
  selectedStudent,
  classType,
  sectionType,
  studentType,
  fromDate,
  toDate,
  schoolData,
  errorMessage,
  setErrorMessage,
  setSelectedClass,
  setSelectedSection,
  setSelectedStudent,
  setClassType,
  setSectionType,
  setStudentType,
  setFromDate,
  setToDate,
  fetchDdlClass,
  fetchDdlSection,
  fetchDdlStudent,
  fetchSchoolDetails,
  generateReport,
  generateCSV,
}) => {
  const handleClassChange = (event) => {
    setSelectedClass(event.target.value);
    setSelectedSection("");
    setSelectedStudent("");
  };

  const handleSectionChange = (event) => {
    setSelectedSection(event.target.value);
    setSelectedStudent("");
  };

  const handleStudentChange = (event) => {
    setSelectedStudent(event.target.value);
  };

  const handleClassTypeChange = (event) => {
    setClassType(event.target.value);
  };

  const handleSectionTypeChange = (event) => {
    setSectionType(event.target.value);
  };

  const handleStudentTypeChange = (event) => {
    setStudentType(event.target.value);
  };

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
  };

  return (
    <Box sx={{ padding: "20px" }}>
      {errorMessage && (
        <Alert severity="error" style={{ marginBottom: "20px", width: "100%" }}>
          {errorMessage}
        </Alert>
      )}
      <Paper elevation={3} style={{ padding: "20px", width: "100%" }}>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <FormControl component="fieldset" style={{ marginBottom: "10px" }}>
            <RadioGroup
              aria-label="classType"
              value={classType}
              onChange={handleClassTypeChange}
            >
              <Box display="flex">
                <FormControlLabel
                  value="allclass"
                  control={<Radio />}
                  label="All Class"
                />
                <FormControlLabel
                  value="classWiseStudent"
                  control={<Radio />}
                  label="Class Wise Student"
                />
              </Box>
            </RadioGroup>
          </FormControl>

          {classType === "classWiseStudent" && (
            <FormControl style={{ marginBottom: "10px", minWidth: "200px" }}>
              <Select
                value={selectedClass}
                onChange={handleClassChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Class
                </MenuItem>
                {ddlClass.map((classItem) => (
                  <MenuItem key={classItem.classId} value={classItem.classId}>
                    {classItem.className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl component="fieldset" style={{ marginBottom: "10px" }}>
            <RadioGroup
              aria-label="sectionType"
              value={sectionType}
              onChange={handleSectionTypeChange}
            >
              <Box display="flex">
                <FormControlLabel
                  value="allSection"
                  control={<Radio />}
                  label="All Section"
                />
                <FormControlLabel
                  value="selectSection"
                  control={<Radio />}
                  label="Select Section"
                />
              </Box>
            </RadioGroup>
          </FormControl>

          {sectionType === "selectSection" && (
            <FormControl style={{ marginBottom: "10px", minWidth: "200px" }}>
              <Select
                value={selectedSection}
                onChange={handleSectionChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Section
                </MenuItem>
                {ddlSection.map((sectionItem) => (
                  <MenuItem
                    key={sectionItem.sectionId}
                    value={sectionItem.sectionId}
                  >
                    {sectionItem.sectionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl component="fieldset" style={{ marginBottom: "10px" }}>
            <RadioGroup
              aria-label="studentType"
              value={studentType}
              onChange={handleStudentTypeChange}
            >
              <Box display="flex">
                <FormControlLabel
                  value="allStudent"
                  control={<Radio />}
                  label="All Student"
                />
                <FormControlLabel
                  value="selectStudent"
                  control={<Radio />}
                  label="Select Student"
                />
              </Box>
            </RadioGroup>
          </FormControl>

          {studentType === "selectStudent" && (
            <FormControl style={{ marginBottom: "10px", minWidth: "200px" }}>
              <Select
                value={selectedStudent}
                onChange={handleStudentChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Student
                </MenuItem>
                {ddlStudent.map((studentItem) => (
                  <MenuItem
                    key={studentItem.studentId}
                    value={studentItem.studentId}
                  >
                    {studentItem.studentName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box display="flex" alignItems="center">
            <TextField
              id="fromDate"
              label="From Date"
              type="date"
              value={fromDate}
              onChange={handleFromDateChange}
              InputLabelProps={{ shrink: true }}
              style={{ marginRight: "10px" }}
            />
            <TextField
              id="toDate"
              label="To Date"
              type="date"
              value={toDate}
              onChange={handleToDateChange}
              InputLabelProps={{ shrink: true }}
              style={{ marginRight: "10px" }}
            />
            <Box display="flex">
              <Button
                variant="contained"
                color="primary"
                onClick={generateReport}
                style={{ marginRight: "10px", width: "120px" }}
              >
                Generate Report
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={generateCSV}
                style={{ width: "120px" }}
              >
                Download CSV
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

const EmployeeAttendance = ({
  fromDate,
  toDate,
    fromTime,
  toTime,
  setFromTime,
  setToTime,
  errorMessage,
  setErrorMessage,
  setFromDate,
  setToDate,
  generateEmployeeReport,
  generateEmployeeCSV,
  ddlEmployee,
  ddlSession,
  ddlDepartment,
  ddlDesignation,
  selectedEmployee,
  selectedSession,
  selectedDepartment,
  selectedDesignation,
  setSelectedEmployee,
  setSelectedSession,
  setSelectedDepartment,
  setSelectedDesignation,
  reportType,
  setReportType,
}) => {

     const handleReportTypeChange = (event) => {
    setReportType(event.target.value);
  };

 const handleEmployeeChange = (event) => {
  const empId = event.target.value;
  setSelectedEmployee(empId);

  if (empId) {
    const selectedEmp = ddlEmployee.find(
      (emp) => emp.employeeID === parseInt(empId)
    );
    if (selectedEmp) {
      // Find matching department by name to get its ID
      const matchedDept = ddlDepartment.find(
        (dept) => dept.departmentName === selectedEmp.departmentName
      );
      if (matchedDept) {
        setSelectedDepartment(matchedDept.deptId);
      }

      // Find matching designation by name to get its ID
      const matchedDesig = ddlDesignation.find(
        (desig) => desig.designationName === selectedEmp.designationName
      );
      if (matchedDesig) {
        setSelectedDesignation(matchedDesig.desigId);
      }
    }
  } else {
    setSelectedDepartment("");
    setSelectedDesignation("");
  }
};

  const handleSessionChange = (event) => {
    console.log("Selected Session ID:", event.target.value);
    setSelectedSession(event.target.value);
  };

  const handleDepartmentChange = (event) => {
    console.log("Selected Department ID:", event.target.value);
    setSelectedDepartment(event.target.value);
    setSelectedDesignation("");
  };

  const handleDesignationChange = (event) => {
    console.log("Selected Designation ID:", event.target.value);
    setSelectedDesignation(event.target.value);
  };

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
  };

  return (
    <Box sx={{ padding: "20px" }}>
      {errorMessage && (
        <Alert severity="error" style={{ marginBottom: "20px", width: "100%" }}>
          {errorMessage}
        </Alert>
      )}
      <Paper elevation={3} style={{ padding: "20px", width: "100%" }}>
         <FormControl component="fieldset" style={{ marginBottom: "10px" }}>
        <RadioGroup
          aria-label="reportType"
          value={reportType}
          onChange={handleReportTypeChange}
          row
        >
          <FormControlLabel
            value="summary"
            control={<Radio />}
            label="Summary Report"
          />
          <FormControlLabel
            value="detail"
            control={<Radio />}
            label="Detailed Report"
          />
        </RadioGroup>
      </FormControl>
      <Box display="flex" flexDirection="column" gap={3} sx={{ padding: 2 }}>
  {/* Dropdowns Section */}
  <Box 
    display="grid" 
    gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} 
    gap={2}
  >
    <FormControl fullWidth>
      <Select
        value={selectedEmployee}
        onChange={handleEmployeeChange}
        displayEmpty
        sx={{ minHeight: '56px' }}
      >
        <MenuItem value="">
          All Employees
        </MenuItem>
        {ddlEmployee.map((employee) => (
          <MenuItem key={employee.employeeID} value={employee.employeeID}>
            {employee.employeeName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <FormControl fullWidth>
      <Select
        value={selectedSession}
        onChange={handleSessionChange}
        displayEmpty
        sx={{ minHeight: '56px' }}
      >
       <MenuItem value="">
          All Sessions
        </MenuItem>
        {ddlSession.map((session) => (
          <MenuItem key={session.financialYearID} value={session.financialYearID}>
            {session.finanacialYear}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <FormControl fullWidth>
      <Select
        value={selectedDepartment}
        onChange={handleDepartmentChange}
        displayEmpty
        sx={{ minHeight: '56px' }}
      >
        <MenuItem value="">
          All Departments
        </MenuItem>
        {ddlDepartment.map((department) => (
          <MenuItem key={department.deptId} value={department.deptId}>
            {department.departmentName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <FormControl fullWidth>
      <Select
        value={selectedDesignation}
        onChange={handleDesignationChange}
        disabled = {!selectedDepartment}
        displayEmpty
        sx={{ minHeight: '56px' }}
      >
       <MenuItem value="">
          All Designations
        </MenuItem>
       {ddlDesignation
  .filter((designation) => 
    !selectedDepartment || designation.departmentId === parseInt(selectedDepartment)
  )
  .map((designation) => (
    <MenuItem key={designation.desigId} value={designation.desigId}>
      {designation.designationName}
    </MenuItem>
  ))
}
      </Select>
    </FormControl>
  </Box>

  {/* Date and Time Filters Section */}
  <Box 
    display="grid" 
    gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} 
    gap={2}
  >
    <TextField
      id="fromDate"
      label="From Date"
      type="date"
      value={fromDate}
      onChange={handleFromDateChange}
      InputLabelProps={{ shrink: true }}
      fullWidth
    />
    
    <TextField
      id="toDate"
      label="To Date"
      type="date"
      value={toDate}
      onChange={handleToDateChange}
      InputLabelProps={{ shrink: true }}
      fullWidth
    />

    <TextField
      id="fromTime"
      label="From Time"
      type="time"
      value={fromTime}
      onChange={(e) => setFromTime(e.target.value)}
      InputLabelProps={{ shrink: true }}
      fullWidth
    />

    <TextField
      id="toTime"
      label="To Time"
      type="time"
      value={toTime}
      onChange={(e) => setToTime(e.target.value)}
      InputLabelProps={{ shrink: true }}
      fullWidth
    />
  </Box>

  {/* Action Buttons Section */}
  <Box display="flex" gap={2} justifyContent={{ xs: 'stretch', sm: 'flex-start' }} flexWrap="wrap">
    <Button
      variant="contained"
      color="primary"
      onClick={generateEmployeeReport}
      sx={{ minWidth: '150px', flex: { xs: 1, sm: 0 } }}
    >
      Generate Report
    </Button>
    <Button
      variant="contained"
      color="error"
      onClick={generateEmployeeCSV}
      sx={{ minWidth: '150px', flex: { xs: 1, sm: 0 } }}
    >
      Download CSV
    </Button>
  </Box>
</Box>
      </Paper>
    </Box>
  );
};

 

const AttendanceReport = () => {
  const [fromTime, setFromTime] = useState("00:00");
const [toTime, setToTime] = useState("00:00");
   const [reportType, setReportType] = useState("detail"); // "summary" or "detail"
  const [ddlEmployee, setDdlEmployee] = useState([]);
const [ddlSession, setDdlSession] = useState([]);
const [ddlDepartment, setDdlDepartment] = useState([]);
const [ddlDesignation, setDdlDesignation] = useState([]);
const [selectedEmployee, setSelectedEmployee] = useState("");
const [selectedSession, setSelectedSession] = useState("");
const [selectedDepartment, setSelectedDepartment] = useState("");
const [selectedDesignation, setSelectedDesignation] = useState("");
const [branchId, setBranchId] = useState("");

  const [ddlClass, setDdlClass] = useState([]);
  const [ddlSection, setDdlSection] = useState([]);
  const [ddlStudent, setDdlStudent] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [classType, setClassType] = useState("allclass");
  const [sectionType, setSectionType] = useState("allSection");
  const [studentType, setStudentType] = useState("allStudent");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reportData, setReportData] = useState(null);
  const [schoolData, setSchoolData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  // ── Info dialog state ──
  const [infoOpen, setInfoOpen] = useState(false);


 const fetchDdlEmployee = useCallback(async () => {
  try {
    const storedBranchId = sessionStorage.getItem("branchId") || 1;
    const apiUrl = `${process.env.REACT_APP_BASE_URL}/Employee/GetEmployee?Branch_Id=${storedBranchId}`;
    const token = sessionStorage.getItem("token");
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch employees: ${response.status}`);
    }
    const data = await response.json();
    setDdlEmployee(Array.isArray(data) ? data : []);
  } catch (error) {
    setErrorMessage(`Error fetching employees: ${error.message}`);
    setDdlEmployee([]);
  }
}, []);

const fetchDdlSession = useCallback(async () => {
  try {
    const apiUrl = `${process.env.REACT_APP_BASE_URL}/ClassPromotion/GetFinancialYear`;
    const token = sessionStorage.getItem("token");
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch sessions: ${response.status}`);
    }
    const data = await response.json();
    setDdlSession(data);
  } catch (error) {
    setErrorMessage(`Error fetching sessions: ${error.message}`);
  }
}, []);

const fetchDdlDepartment = useCallback(async () => {
  try {
    const apiUrl = `${process.env.REACT_APP_BASE_URL}/Department/DepartmentName`;
    const token = sessionStorage.getItem("token");
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch departments: ${response.status}`);
    }
    const data = await response.json();
    setDdlDepartment(Array.isArray(data) ? data : []);
  } catch (error) {
    setErrorMessage(`Error fetching departments: ${error.message}`);
  }
}, []);

const fetchDdlDesignation = useCallback(async () => {
  try {
    const apiUrl = `${process.env.REACT_APP_BASE_URL}/Designation/DesignationName`;
    const token = sessionStorage.getItem("token");
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch designations: ${response.status}`);
    }
    const data = await response.json();
    setDdlDesignation(Array.isArray(data) ? data : []);
  } catch (error) {
    setErrorMessage(`Error fetching designations: ${error.message}`); 
  }
}, []);

// In the AttendanceReport component, modify the useEffect that sets fromDate and toDate:

useEffect(() => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const currentTime = `${hours}:${minutes}`;
  
  setFromDate(getFirstDayOfMonth());
  setToDate(getCurrentDate());
  setFromTime(currentTime);  // Add this line
  setToTime(currentTime);    // Add this line
}, []);

useEffect(() => {
  const branchId = sessionStorage.getItem("branchId");
  if (branchId) {
    setBranchId(parseInt(branchId));
  }
}, []);

useEffect(() => {
  fetchDdlEmployee();
  fetchDdlSession();
  fetchDdlDepartment();
  fetchDdlDesignation();
}, [fetchDdlEmployee, fetchDdlSession, fetchDdlDepartment, fetchDdlDesignation]);

  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .split("T")[0];
  };

  const getCurrentDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  useEffect(() => {
    setFromDate(getFirstDayOfMonth());
    setToDate(getCurrentDate());
  }, []);

  const fetchSchoolDetails = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/FeeReport/GetSchoolName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch school details: ${response.status}`);
      }
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        setErrorMessage("School details not found.");
        return;
      }
      setSchoolData(data);
    } catch (error) {
      setErrorMessage(`Error fetching school details: ${error.message}`);
    }
  }, []);

  const fetchDdlClass = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch classes: ${response.status}`);
      }
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        setErrorMessage("No classes found.");
        return;
      }
      setDdlClass(data);
    } catch (error) {
      setErrorMessage(`Error fetching classes: ${error.message}`);
    }
  }, []);

  const fetchDdlSection = useCallback(async (classId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Exam/ddlSection_clsId`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ classId, teacherId: 0 }),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.status}`);
      }
      const responseData = await response.json();
      if (responseData && responseData.data === null) {
        setDdlSection([]);
      } else {
        setDdlSection(responseData);
      }
    } catch (error) {
      setErrorMessage(`Error fetching sections: ${error.message}`);
    }
  }, []);

  const fetchDdlStudent = useCallback(async (classId, sectionId) => {
    try {
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/FeeReport/ddlStudentbyClassId`;
      const token = sessionStorage.getItem("token");
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ classId, sectionId }),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch students: ${response.status}`);
      }
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        setErrorMessage("No students found.");
        return;
      }
      setDdlStudent(data);
    } catch (error) {
      setErrorMessage(`Error fetching students: ${error.message}`);
    }
  }, []);

  useEffect(() => {
    fetchSchoolDetails();
    fetchDdlClass();
  }, [fetchSchoolDetails, fetchDdlClass]);

  useEffect(() => {
    if (selectedClass !== "") {
      fetchDdlSection(selectedClass);
    }
  }, [selectedClass, fetchDdlSection]);

  useEffect(() => {
    if (selectedClass !== "" && selectedSection !== "") {
      fetchDdlStudent(selectedClass, selectedSection);
    }
  }, [selectedClass, selectedSection, fetchDdlStudent]);

  const formatDate = (dateString) => {
  return new Date(dateString).toISOString();
};

const formatDateDDMMYY = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}/${month}/${year}`;
};

  const generateReport = async () => {
  setErrorMessage("");
  try {
    const url = process.env.REACT_APP_BASE_URL;
    const apiUrl = `${url}/FeeReport/ReportAttendence`;
    const token = sessionStorage.getItem("token");

    const formattedFromDate = formatDate(fromDate);
    const formattedToDate = formatDate(toDate);

  const requestBody = {
  isAllClass: classType === "allclass" || classType === "",
  isAllSection: sectionType === "allSection" || sectionType === "",
  isAllStudent: studentType === "allStudent" || studentType === "",
  classId: classType === "classWiseStudent" && selectedClass ? parseInt(selectedClass) : 0,
  sectionId: sectionType === "selectSection" && selectedSection ? parseInt(selectedSection) : 0,
  studentId: studentType === "selectStudent" && selectedStudent ? parseInt(selectedStudent) : 0,
  classTypeId: 0, // Add this — send 0 as default or wire up a selector if needed
  fromDate: formatDateDDMMYY(fromDate),
toDate: formatDateDDMMYY(toDate),
};

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();

    if (data.msg === "Record Not Found") {
      setErrorMessage(
        "No attendance records found for the selected criteria."
      );
      return;
    }

    setReportData(data);

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    let yOffset = 15;

    // Process logo
    let logoDataUrl = null;
    try {
      // Hardcoded logo as fallback
      const hardcodedLogo = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=';
      logoDataUrl = hardcodedLogo;

      // Validate hardcoded image
      const img = new Image();
      const loadPromise = new Promise((resolve, reject) => {
        img.onload = () => {
          console.log("Hardcoded logo loaded successfully");
          resolve();
        };
        img.onerror = () => {
          console.error("Failed to load hardcoded logo image");
          reject(new Error('Failed to load hardcoded logo image'));
        };
        img.src = logoDataUrl;
      });
      await loadPromise;

      try {
        // Add white background with subtle shadow
        doc.setFillColor(240, 240, 240);
        doc.rect(margin - 2, 5, 30, 30, "F"); // Shadow background
        doc.setFillColor(255, 255, 255);
        doc.rect(margin, 7, 26, 26, "F"); // White background
        // Add hardcoded logo
        doc.addImage(logoDataUrl, 'PNG', margin + 2, 9, 22, 22);
        console.log("Hardcoded logo successfully added to PDF");
      } catch (error) {
        console.error('Error adding hardcoded logo to PDF:', error.message);
        throw error;
      }

      // Attempt API fetch for actual logo
      if (token) {
        const logoResponse = await fetch('https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token,
          },
          body: JSON.stringify({}),
        });

        if (!logoResponse.ok) {
          console.error(`Logo API fetch failed: ${logoResponse.status} ${logoResponse.statusText}`);
          throw new Error(`Logo API error: ${logoResponse.status} ${logoResponse.statusText}`);
        }

        const logoApiData = await logoResponse.json();
        console.log("Logo API response data:", JSON.stringify(logoApiData, null, 2));

        const logoImg = logoApiData?.[0]?.logoImg;
        if (!logoImg || typeof logoImg !== 'string' || logoImg.trim() === '') {
          console.warn("No valid logo image found in API response");
          throw new Error("Invalid or missing logoImg in API response");
        }

        let cleanedBase64 = logoImg
          .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
          .replace(/\s/g, '')
          .replace(/\n/g, '');
        const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
        cleanedBase64 += '='.repeat(paddingLength);

        const isValidBase64 = /^[A-Za-z0-9+/=]+$/.test(cleanedBase64);
        if (!isValidBase64) {
          console.error('Invalid base64 string for logoImg:', cleanedBase64.substring(0, 100) + '...');
          throw new Error('Invalid base64 string for logoImg');
        }

        console.log('Logo base64 (first 100 chars):', cleanedBase64.substring(0, 100) + '...');
        console.log('Full logo base64 length:', cleanedBase64.length);

        logoDataUrl = `data:image/png;base64,${cleanedBase64}`;

        // Validate API image
        const apiImg = new Image();
        const apiLoadPromise = new Promise((resolve, reject) => {
          apiImg.onload = () => {
            console.log("API logo loaded successfully");
            resolve();
          };
          apiImg.onerror = () => {
            console.error("Failed to load API logo image");
            reject(new Error('Failed to load API logo image'));
          };
          apiImg.src = logoDataUrl;
        });
        await apiLoadPromise;

        try {
          // Add white background with subtle shadow
          doc.setFillColor(240, 240, 240);
          doc.rect(margin - 2, 5, 30, 30, "F"); // Shadow background
          doc.setFillColor(255, 255, 255);
          doc.rect(margin, 7, 26, 26, "F"); // White background
          // Add API logo
          doc.addImage(logoDataUrl, 'PNG', margin + 2, 9, 22, 22);
          console.log("API logo successfully added to PDF");
        } catch (error) {
          console.error('Error adding API logo to PDF:', error.message);
          throw error;
        }
      } else {
        console.warn("No authentication token found in sessionStorage");
        throw new Error("No authentication token found");
      }
    } catch (error) {
      console.error('Error fetching or processing logo:', error.message);
      // Add debug text to indicate logo failure
      doc.setFont("times", "bold");
      doc.setFontSize(10);
      doc.setTextColor(150, 0, 0);
      doc.text("Logo Failed", margin, 50);
      doc.setTextColor(0, 0, 0);
      yOffset = 55; // Adjust yOffset to account for logo failure
    }

    // Adding school header
    if (schoolData.length > 0) {
      const school = schoolData[0];
      doc.setFontSize(18);
      doc.setFont("times", "bold");
      doc.setTextColor(0, 51, 102);
      doc.text(school.schoolName, pageWidth / 2, yOffset, {
        align: "center",
      });
      yOffset += 8;
      doc.setLineWidth(0.4);
      doc.setDrawColor(0, 51, 102);
      doc.line(margin + 40, yOffset, pageWidth - margin - 40, yOffset);
      yOffset += 8;
      doc.setFontSize(14);
      doc.setFont("times", "normal");
      doc.setTextColor(0, 51, 102);
      doc.text("Attendance Report", pageWidth / 2, yOffset, {
        align: "center",
      });
      yOffset += 8;
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(
        `From: ${formattedFromDate} To: ${formattedToDate}`,
        pageWidth / 2,
        yOffset,
        { align: "center" }
      );
      yOffset += 8;
    }

    // Optimize table data preparation with Serial No
    const tableData = data.map((item, index) => [
      (index + 1).toString(),
      item.studentName || "",
      item.rollNo || "",
      item.className || "",
      item.section || "",
      item.attendenceStatus || "",
      item.attendenceDate || "",
    ]);

    // Calculate total table width to ensure it fits within margins
    const availableWidth = pageWidth - 2 * margin;
    const columnWidths = {
      serialNo: 15,
      studentName: 40,
      rollNo: 20,
      class: 25,
      section: 25,
      attendanceStatus: 30,
      attendanceDate: 30,
    };
    const totalColumnWidth = Object.values(columnWidths).reduce(
      (sum, width) => sum + width,
      0
    );

    // Adding table with centered alignment
    doc.autoTable({
      startY: yOffset,
      head: [
        [
          "Serial No",
          "Student Name",
          "Roll No",
          "Class",
          "Section",
          "Attendance Status",
          "Attendance Date",
        ],
      ],
      body: tableData,
      theme: "grid",
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: [0, 51, 102],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: "bold",
        font: "times",
        cellPadding: 1.5,
      },
      bodyStyles: {
        fontSize: 8,
        font: "times",
        cellPadding: 1,
        textColor: [0, 0, 0],
        minCellHeight: 4,
      },
      columnStyles: {
        0: { cellWidth: columnWidths.serialNo },
        1: { cellWidth: columnWidths.studentName },
        2: { cellWidth: columnWidths.rollNo },
        3: { cellWidth: columnWidths.class },
        4: { cellWidth: columnWidths.section },
        5: { cellWidth: columnWidths.attendanceStatus },
        6: { cellWidth: columnWidths.attendanceDate },
      },
      didParseCell: (data) => {
        data.cell.styles.valign = "middle";
        data.cell.styles.halign = data.column.index === 0 ? "center" : "left";
      },
      tableWidth: Math.min(availableWidth, totalColumnWidth),
    });

    // Generate PDF blob and open in new tab
    const pdfBlob = await doc.output("blob", { returnPromise: true });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
  } catch (error) {
    setErrorMessage(`Error generating report: ${error.message}`);
  }
};
  
  const generateCSV = () => {
    setErrorMessage("");
    if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
      setErrorMessage("No report data available to download as CSV.");
      return;
    }

    const keys = Object.keys(reportData[0]);
    const csvContent = [
      keys.map((key) => key.toUpperCase()).join(","),
      ...reportData.map((item) => keys.map((key) => item[key] || "").join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "report.csv");
  };

 const generateEmployeeReport = async () => {
  setErrorMessage("");
  try {
    const url = process.env.REACT_APP_BASE_URL;
    const apiUrl = `${url}/EmployeeAttendance/GetEmployeeAttendanceReport`;
    const token = sessionStorage.getItem("token");

    const requestBody = {
      employeeId: selectedEmployee ? parseInt(selectedEmployee) : 0,
   fromDate: `${fromDate}T${fromTime ? fromTime + ":00.000Z" : "00:00:00.000Z"}`,
  toDate: `${toDate}T${toTime ? toTime + ":59.999Z" : "23:59:59.999Z"}`,
      branchId: branchId ? parseInt(branchId) : 0,
      sessionId: selectedSession ? parseInt(selectedSession) : 0,
      departmentId: selectedDepartment ? parseInt(selectedDepartment) : 0,
      designationId: selectedDesignation ? parseInt(selectedDesignation) : 0,
    };

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();

    if (data.msg === "Record Not Found") {
      setErrorMessage(
        "No employee attendance records found for the selected criteria."
      );
      return;
    }

    setReportData(data);

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    let yOffset = 15;

    // Add logo
// Replace the entire logo section in generateEmployeeReport (around lines 780-795)
// with this complete implementation:

// Process logo
let logoDataUrl = null;
try {
  // Hardcoded logo as fallback
  const hardcodedLogo = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=';
  logoDataUrl = hardcodedLogo;

  // Validate hardcoded image
  const img = new Image();
  const loadPromise = new Promise((resolve, reject) => {
    img.onload = () => {
      console.log("Hardcoded logo loaded successfully");
      resolve();
    };
    img.onerror = () => {
      console.error("Failed to load hardcoded logo image");
      reject(new Error('Failed to load hardcoded logo image'));
    };
    img.src = logoDataUrl;
  });
  await loadPromise;

  try {
    // Add white background with subtle shadow
    doc.setFillColor(240, 240, 240);
    doc.rect(margin - 2, 5, 30, 30, "F"); // Shadow background
    doc.setFillColor(255, 255, 255);
    doc.rect(margin, 7, 26, 26, "F"); // White background
    // Add hardcoded logo
    doc.addImage(logoDataUrl, 'PNG', margin + 2, 9, 22, 22);
    console.log("Hardcoded logo successfully added to PDF");
  } catch (error) {
    console.error('Error adding hardcoded logo to PDF:', error.message);
    throw error;
  }

  // Attempt API fetch for actual logo
  if (token) {
    const logoResponse = await fetch('https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
      body: JSON.stringify({}),
    });

    if (!logoResponse.ok) {
      console.error(`Logo API fetch failed: ${logoResponse.status} ${logoResponse.statusText}`);
      throw new Error(`Logo API error: ${logoResponse.status} ${logoResponse.statusText}`);
    }

    const logoApiData = await logoResponse.json();
    console.log("Logo API response data:", JSON.stringify(logoApiData, null, 2));

    const logoImg = logoApiData?.[0]?.logoImg;
    if (!logoImg || typeof logoImg !== 'string' || logoImg.trim() === '') {
      console.warn("No valid logo image found in API response");
      throw new Error("Invalid or missing logoImg in API response");
    }

    let cleanedBase64 = logoImg
      .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
      .replace(/\s/g, '')
      .replace(/\n/g, '');
    const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
    cleanedBase64 += '='.repeat(paddingLength);

    const isValidBase64 = /^[A-Za-z0-9+/=]+$/.test(cleanedBase64);
    if (!isValidBase64) {
      console.error('Invalid base64 string for logoImg:', cleanedBase64.substring(0, 100) + '...');
      throw new Error('Invalid base64 string for logoImg');
    }

    console.log('Logo base64 (first 100 chars):', cleanedBase64.substring(0, 100) + '...');
    console.log('Full logo base64 length:', cleanedBase64.length);

    logoDataUrl = `data:image/png;base64,${cleanedBase64}`;

    // Validate API image
    const apiImg = new Image();
    const apiLoadPromise = new Promise((resolve, reject) => {
      apiImg.onload = () => {
        console.log("API logo loaded successfully");
        resolve();
      };
      apiImg.onerror = () => {
        console.error("Failed to load API logo image");
        reject(new Error('Failed to load API logo image'));
      };
      apiImg.src = logoDataUrl;
    });
    await apiLoadPromise;

    try {
      // Add white background with subtle shadow
      doc.setFillColor(240, 240, 240);
      doc.rect(margin - 2, 5, 30, 30, "F"); // Shadow background
      doc.setFillColor(255, 255, 255);
      doc.rect(margin, 7, 26, 26, "F"); // White background
      // Add API logo
      doc.addImage(logoDataUrl, 'PNG', margin + 2, 9, 22, 22);
      console.log("API logo successfully added to PDF");
    } catch (error) {
      console.error('Error adding API logo to PDF:', error.message);
      throw error;
    }
  } else {
    console.warn("No authentication token found in sessionStorage");
    throw new Error("No authentication token found");
  }
} catch (error) {
  console.error('Error fetching or processing logo:', error.message);
  // Add debug text to indicate logo failure
  doc.setFont("times", "bold");
  doc.setFontSize(10);
  doc.setTextColor(150, 0, 0);
  doc.text("Logo Failed", margin, 50);
  doc.setTextColor(0, 0, 0);
  yOffset = 55; // Adjust yOffset to account for logo failure
}
    // Add school header
    if (schoolData.length > 0) {
      const school = schoolData[0];
      doc.setFontSize(18);
      doc.setFont("times", "bold");
      doc.setTextColor(0, 51, 102);
      doc.text(school.schoolName, pageWidth / 2, yOffset, { align: "center" });
      yOffset += 8;
      doc.setLineWidth(0.4);
      doc.setDrawColor(0, 51, 102);
      doc.line(margin + 40, yOffset, pageWidth - margin - 40, yOffset);
      yOffset += 8;
      doc.setFontSize(14);
      doc.setFont("times", "normal");
      doc.setTextColor(0, 51, 102);
      doc.text(
        `${reportType === "summary" ? "Summary" : "Detailed"} Employee Attendance Report`,
        pageWidth / 2,
        yOffset,
        { align: "center" }
      );
      yOffset += 8;
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(
        `From: ${fromDate} To: ${toDate}`,
        pageWidth / 2,
        yOffset,
        { align: "center" }
      );
      yOffset += 8;
    }

    // Prepare table data based on report type
    let tableData;
    let tableColumns;

    if (reportType === "summary") {
      tableColumns = [
        ["S. No.", "Emp Name", "Emp Code", "Dept.", "Desig.", "Month", "Present", "Absent", "Leave", "Work Days", "Present %"],
      ];
      tableData = data.summaryReport.map((item, index) => [
        (index + 1).toString(),
        item.employeeName || "",
        item.employeeCode || "",
        item.departmentName || "",
        item.designationName || "",
        item.month || "",
        item.presentCount || "",
        item.absentCount || "",
        item.leaveCount || "",
        item.workingDays || "",
        item.presentPercentage || "",
      ]);
    } else {
      tableColumns = [
        ["S. No.", "Emp Name", "Emp Code", "Dept", "Desig", "Attendance Date", "Day", "Status"],
      ];
      tableData = data.detailReport.map((item, index) => [
        (index + 1).toString(),
        item.employeeName || "",
        item.employeeCode || "",
        item.departmentName || "",
        item.designationName || "",
        item.attendanceDate || "",
        item.dayName || "",
        item.attendanceStatus || "",
      ]);
    }

    // Calculate column widths
    const availableWidth = pageWidth - 2 * margin;
    const columnWidths = {
      serialNo: 10,
      employeeName: 25,
      employeeCode: 15,
      departmentName: 20,
      designationName: 20,
      month: 20,
      present: 15,
      absent: 15,
      leave: 15,
      workingDays: 15,
      presentPercentage: 15,
      attendanceDate: 25,
      dayName: 20,
      attendanceStatus: 20,
    };

  // Only include the columns actually used by the current report type
const usedKeys = reportType === "summary"
  ? ["serialNo", "employeeName", "employeeCode", "departmentName", "designationName", "month", "present", "absent", "leave", "workingDays", "presentPercentage"]
  : ["serialNo", "employeeName", "employeeCode", "departmentName", "designationName", "attendanceDate", "dayName", "attendanceStatus"];

const totalColumnWidth = usedKeys.reduce(
  (sum, key) => sum + columnWidths[key],
  0
);

// Scale only the used columns proportionally so the table fills the full page width
const scaleFactor = availableWidth / totalColumnWidth;
usedKeys.forEach((key) => {
  columnWidths[key] = columnWidths[key] * scaleFactor;
});


    // Add table to PDF
    doc.autoTable({
      startY: yOffset,
      head: tableColumns,
      body: tableData,
      theme: "grid",
margin: { left: margin, right: margin },
      headStyles: {
        fillColor: [0, 51, 102],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: "bold",
        font: "times",
        cellPadding: 1.5,
      },
      bodyStyles: {
        fontSize: 8,
        font: "times",
        cellPadding: 1,
        textColor: [0, 0, 0],
        minCellHeight: 4,
      },
      columnStyles: {
        0: { cellWidth: columnWidths.serialNo },
        1: { cellWidth: columnWidths.employeeName },
        2: { cellWidth: columnWidths.employeeCode },
        3: { cellWidth: columnWidths.departmentName },
        4: { cellWidth: columnWidths.designationName },
        5: { cellWidth: reportType === "summary" ? columnWidths.month : columnWidths.attendanceDate },
        6: { cellWidth: reportType === "summary" ? columnWidths.present : columnWidths.dayName },
        7: { cellWidth: reportType === "summary" ? columnWidths.absent : columnWidths.attendanceStatus },
        8: { cellWidth: reportType === "summary" ? columnWidths.leave : undefined },
        9: { cellWidth: reportType === "summary" ? columnWidths.workingDays : undefined },
        10: { cellWidth: reportType === "summary" ? columnWidths.presentPercentage : undefined },
      },
      didParseCell: (data) => {
        data.cell.styles.valign = "middle";
        data.cell.styles.halign = data.column.index === 0 ? "center" : "left";
      },
tableWidth: availableWidth,
    });

    // Generate PDF blob and open in new tab
    const pdfBlob = await doc.output("blob", { returnPromise: true });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
  } catch (error) {
    setErrorMessage(`Error generating employee report: ${error.message}`);
  }
};
 


 const generateEmployeeCSV = () => {
  setErrorMessage("");
  if (!reportData || (reportType === "summary" ? !reportData.summaryReport : !reportData.detailReport) || (reportType === "summary" ? reportData.summaryReport.length === 0 : reportData.detailReport.length === 0)) {
    setErrorMessage(`No employee ${reportType} report data available to download as CSV.`);
    return;
  }

  const report = reportType === "summary" ? reportData.summaryReport : reportData.detailReport;
  const keys = Object.keys(report[0]);
  const csvContent = [
    keys.map((key) => key.toUpperCase()).join(","),
    ...report.map((item) => keys.map((key) => item[key] || "").join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
  saveAs(blob, `${reportType}_employee_report.csv`);
};


  return (
    <Container>
      {/* <Box
        sx={{
          backgroundColor: '#0B1F40',
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          width: "1200px",
        }}
      > */}
       <AppBar position="static" style={{ backgroundColor: "#0B1F40" }}>
        <Toolbar>
          <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold', flexGrow: 1 }}>
            Attendance Report
          </Typography>

          {/* ── Info Button ── */}
          <IconButton
            color="inherit"
            onClick={() => setInfoOpen(true)}
            title="Report Information"
            style={{
              backgroundColor: "rgba(255,255,255,0.15)",
              border: "2px solid rgba(255,255,255,0.6)",
              borderRadius: "50%",
              padding: "6px",
            }}
          >
            <InfoIcon style={{ fontSize: 26 }} />
          </IconButton>
        </Toolbar>
      </AppBar>
      {/* </Box> */}

      {/* ── Info Dialog ── */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, padding: 0 } }}
      >
        <DialogTitle
          style={{
            backgroundColor: "#0B1F40",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
          }}
        >
          <Box display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Attendance Reports — Overview
            </Typography>
          </Box>
        
        </DialogTitle>

        <DialogContent dividers style={{ backgroundColor: "#f9f9f9", padding: "24px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
            This module provides attendance reporting for both students and employees. Below is a summary of each report, its filters, and the data columns it displays.
          </Typography>

          {reportInfoData.map((report, index) => (
            <Box
              key={index}
              style={{
                backgroundColor: "#fff",
                borderRadius: 12,
                padding: "18px 20px",
                marginBottom: 16,
                borderLeft: `5px solid ${report.color}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              }}
            >
              <Typography
                variant="h6"
                style={{ fontWeight: "bold", color: report.color, marginBottom: 6 }}
              >
                {report.title}
              </Typography>

              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                {report.description}
              </Typography>

              <Divider style={{ marginBottom: 10 }} />

              {/* Filters */}
              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 4 }}>
                🔍 FILTERS
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} style={{ marginBottom: 10 }}>
                {report.filters.map((f, i) => (
                  <Chip key={i} label={f} size="small" style={{ backgroundColor: "#E3F2FD", color: "#1565C0", fontWeight: 500 }} />
                ))}
              </Box>

              {/* Columns — Employee report has summary/detail split */}
              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 4 }}>
                📋 REPORT COLUMNS
              </Typography>
              {Array.isArray(report.columns) ? (
                <Box display="flex" flexWrap="wrap" gap={1} style={{ marginBottom: 10 }}>
                  {report.columns.map((col, i) => (
                    <Chip key={i} label={col} size="small" variant="outlined" style={{ borderColor: report.color, color: report.color }} />
                  ))}
                </Box>
              ) : (
                <>
                  <Typography variant="caption" style={{ color: "#777", display: "block", marginBottom: 4 }}>
                    Summary Report:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} style={{ marginBottom: 8 }}>
                    {report.columns.summary.map((col, i) => (
                      <Chip key={i} label={col} size="small" variant="outlined" style={{ borderColor: report.color, color: report.color }} />
                    ))}
                  </Box>
                  <Typography variant="caption" style={{ color: "#777", display: "block", marginBottom: 4 }}>
                    Detailed Report:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} style={{ marginBottom: 10 }}>
                    {report.columns.detail.map((col, i) => (
                      <Chip key={i} label={col} size="small" variant="outlined" style={{ borderColor: report.color, color: report.color }} />
                    ))}
                  </Box>
                </>
              )}

              {/* Exports */}
              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 4 }}>
                📤 EXPORT OPTIONS
              </Typography>
              <Box display="flex" gap={1}>
                {report.exports.map((exp, i) => (
                  <Chip
                    key={i}
                    label={exp}
                    size="small"
                    style={{
                      backgroundColor: exp === "PDF" ? "#FFEBEE" : "#E8F5E9",
                      color: exp === "PDF" ? "#C62828" : "#2E7D32",
                      fontWeight: "bold",
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", backgroundColor: "#f9f9f9" }}>
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            style={{ backgroundColor: "#0B1F40", color: "#fff", borderRadius: 20, paddingInline: 28 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="student-attendance-content"
          id="student-attendance-header"
        >
          <Typography variant="h6">Student Attendance</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <StudentAttendance
            ddlClass={ddlClass}
            ddlSection={ddlSection}
            ddlStudent={ddlStudent}
            selectedClass={selectedClass}
            selectedSection={selectedSection}
            selectedStudent={selectedStudent}
            classType={classType}
            sectionType={sectionType}
            studentType={studentType}
            fromDate={fromDate}
            toDate={toDate}
            schoolData={schoolData}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            setSelectedClass={setSelectedClass}
            setSelectedSection={setSelectedSection}
            setSelectedStudent={setSelectedStudent}
            setClassType={setClassType}
            setSectionType={setSectionType}
            setStudentType={setStudentType}
            setFromDate={setFromDate}
            setToDate={setToDate}
            fetchDdlClass={fetchDdlClass}
            fetchDdlSection={fetchDdlSection}
            fetchDdlStudent={fetchDdlStudent}
            fetchSchoolDetails={fetchSchoolDetails}
            generateReport={generateReport}
            generateCSV={generateCSV}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="employee-attendance-content"
          id="employee-attendance-header"
        >
          <Typography variant="h6">Employee Attendance</Typography>
        </AccordionSummary>
        <AccordionDetails>
         <EmployeeAttendance
  fromDate={fromDate}
  toDate={toDate}
      fromTime={fromTime}
  toTime={toTime}
  setFromTime={setFromTime}
  setToTime={setToTime}
  errorMessage={errorMessage}
  setErrorMessage={setErrorMessage}
  setFromDate={setFromDate}
  setToDate={setToDate}
  generateEmployeeReport={generateEmployeeReport}
  generateEmployeeCSV={generateEmployeeCSV}
  ddlEmployee={ddlEmployee}
  ddlSession={ddlSession}
  ddlDepartment={ddlDepartment}
  ddlDesignation={ddlDesignation}
  selectedEmployee={selectedEmployee}
  selectedSession={selectedSession}
  selectedDepartment={selectedDepartment}
  selectedDesignation={selectedDesignation}
  setSelectedEmployee={setSelectedEmployee}
  setSelectedSession={setSelectedSession}
  setSelectedDepartment={setSelectedDepartment}
  setSelectedDesignation={setSelectedDesignation}
   reportType={reportType}
            setReportType={setReportType}
  
/>

        </AccordionDetails>
      </Accordion>
    </Container>
  );
};

export default AttendanceReport;